import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { CheckCircle, Circle, Zap, ChevronDown } from "lucide-react";
import { isSidebarCompactNav } from "@/lib/layout/shellSidebarLayout";

/* ─── pill style helper ─────────────────────────────────────────────────── */
const pillStyle = (achieved, inProgress) => ({
  padding: "4px 8px",
  borderRadius: 8,
  background: achieved
    ? "rgba(34,197,94,0.08)"
    : inProgress
    ? "rgba(234,179,8,0.08)"
    : "rgba(30,41,59,0.5)",
  border: achieved
    ? "1px solid rgba(34,197,94,0.2)"
    : inProgress
    ? "1px solid rgba(234,179,8,0.2)"
    : "1px solid rgba(51,65,85,0.4)",
  flexShrink: 0,
});

/* ─── single criterion pill ─────────────────────────────────────────────── */
const CriterionPill = ({ achieved, inProgress, label, score, maxScore }) => (
  <div className="flex items-center gap-1.5" style={pillStyle(achieved, inProgress)}>
    {achieved ? (
      <CheckCircle style={{ width: 12, height: 12, color: "#4ade80", flexShrink: 0 }} />
    ) : inProgress ? (
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          border: "2px solid #facc15",
          background: "rgba(250,204,21,0.15)",
          flexShrink: 0,
        }}
      />
    ) : (
      <Circle style={{ width: 12, height: 12, color: "#475569", flexShrink: 0 }} />
    )}
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: achieved ? "#4ade80" : inProgress ? "#facc15" : "#9ca3af",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      <span style={{ color: "#9ca3af", fontWeight: 500, marginLeft: 3 }}>
        ({score}/{maxScore})
      </span>
    </span>
  </div>
);

/* ─── divider ───────────────────────────────────────────────────────────── */
const VDiv = () => (
  <div
    style={{
      width: 1,
      height: 18,
      background: "rgba(51,65,85,0.5)",
      flexShrink: 0,
    }}
  />
);

/* ─── main component ────────────────────────────────────────────────────── */
const TokenStrengthBar = ({ strength }) => {
  // Run hooks before destructuring `strength` (undefined threw and skipped hooks).
  const { strengthCriteria, tokenData } = useSelector((s) => s.tokenStrength);
  const { isCollapsed, isHydrated, isMobileSidebarOpen } = useSelector((s) => s.sidebar);

  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false); // mobile expand toggle

  useEffect(() => setMounted(true), []);

  const {
    score = 0,
    level = "Incomplete",
    color = "#64748b",
    details = [],
    tokenCreationScore,
    liquidityPoolScore,
    lockedLiquidityScore,
  } = strength || {};

  const tokenCreated = !!tokenData?.mintAddress || !!strengthCriteria?.tokenExists;
  const liquidityPoolCreated = !!strengthCriteria?.hasLiquidity;
  const liquidityLocked =
    !!strengthCriteria?.liquidityLocked || (lockedLiquidityScore ?? 0) > 0;

  const compactNav = isSidebarCompactNav(isMobileSidebarOpen, isCollapsed);
  const sidebarLeft =
    mounted && isHydrated ? (compactNav ? "lg:left-16" : "lg:left-64") : "lg:left-64";

  const isFourCriteria = Array.isArray(details) && details.length === 4;

  /* derive criteria list for unified rendering */
  const criteria = isFourCriteria
    ? details.map((d) => ({
        id: d.id,
        label: d.name.replace(" Revoked", ""),
        achieved: d.achieved,
        inProgress: false,
        score: d.achieved ? d.points : 0,
        maxScore: d.maxPoints ?? d.points,
      }))
    : [
        {
          id: "token",
          label: "Token",
          achieved: tokenCreated || (tokenCreationScore ?? 0) >= 50,
          inProgress:
            (tokenCreationScore ?? 0) > 0 && (tokenCreationScore ?? 0) < 50,
          score: tokenCreationScore ?? 0,
          maxScore: 80,
        },
        {
          id: "liquidity",
          label: "Liquidity",
          achieved: liquidityPoolCreated,
          inProgress: false,
          score: liquidityPoolScore ?? 0,
          maxScore: 10,
        },
        {
          id: "locked",
          label: "Locked",
          achieved: liquidityLocked,
          inProgress: false,
          score: lockedLiquidityScore ?? 0,
          maxScore: 10,
        },
      ];

  const achievedCount = criteria.filter((c) => c.achieved).length;

  return (
    <div className="relative w-full">
      {/* spacer — matches bar height on desktop / mobile */}
      <div className="h-[52px] sm:h-[52px]" />

      <div
        className={`fixed top-16 left-0 ${sidebarLeft} right-0 z-40 transition-all duration-300`}
      >
        <div
          style={{
            background:
              "linear-gradient(180deg,rgba(15,23,42,0.98) 0%,rgba(15,23,42,0.92) 100%)",
            borderBottom: "1px solid rgba(34,211,238,0.12)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
            backdropFilter: "blur(16px)",
          }}
        >
          {/* ── MAIN ROW ─────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 px-3 sm:px-5 lg:px-8 h-[52px] max-w-7xl mx-auto">

            {/* score block */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Zap style={{ width: 14, height: 14, color: "#22d3ee" }} />

              {/* label — hidden on very small screens */}
              <span
                className="hidden xs:block"
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "#64748b",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                Strength
              </span>

              <div style={{ width: 1, height: 14, background: "rgba(100,116,139,0.3)" }} />

              <div className="flex items-baseline gap-0.5">
                <span
                  style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9", lineHeight: 1 }}
                >
                  {score}
                </span>
                <span style={{ fontSize: 11, color: "#475569", fontWeight: 500 }}>/100</span>
              </div>

              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 20,
                  color,
                  background: `${color}15`,
                  border: `1px solid ${color}35`,
                  letterSpacing: "0.03em",
                  whiteSpace: "nowrap",
                }}
              >
                {level}
              </span>
            </div>

            {/* progress bar — fills remaining space */}
            <div className="flex-1 min-w-0">
              <div
                style={{
                  position: "relative",
                  height: 10,
                  borderRadius: 99,
                  background: "rgba(30,41,59,0.9)",
                  overflow: "hidden",
                  border: "1px solid rgba(51,65,85,0.5)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${Math.min(score, 100)}%`,
                    background: `linear-gradient(90deg,${color}99,${color})`,
                    boxShadow: score > 0 ? `0 0 8px ${color}70` : "none",
                    borderRadius: 99,
                    transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)",
                  }}
                />
                {/* segment dividers */}
                {isFourCriteria ? (
                  [25, 50, 75].map((p) => (
                    <div
                      key={p}
                      style={{
                        position: "absolute",
                        left: `${p}%`,
                        top: 0,
                        width: 1,
                        height: "100%",
                        background: "rgba(100,116,139,0.35)",
                      }}
                    />
                  ))
                ) : (
                  <div
                    style={{
                      position: "absolute",
                      left: "80%",
                      top: 0,
                      width: 1,
                      height: "100%",
                      background: "rgba(100,116,139,0.35)",
                    }}
                  />
                )}
                {/* inline % label */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#e5e7eb",
                    pointerEvents: "none",
                    textShadow: "0 1px 2px rgba(15,23,42,0.9)",
                  }}
                >
                  {Math.round(score)}%
                </div>
              </div>
            </div>

            {/* ── CRITERIA: desktop (md+) — inline pills ── */}
            <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
              {criteria.map((c, i) => (
                <React.Fragment key={c.id}>
                  {i > 0 && <VDiv />}
                  <CriterionPill
                    achieved={c.achieved}
                    inProgress={c.inProgress}
                    label={c.label}
                    score={c.score}
                    maxScore={c.maxScore}
                  />
                </React.Fragment>
              ))}
            </div>

            {/* ── CRITERIA: mobile — compact summary + toggle ── */}
            <button
              className="flex md:hidden items-center gap-1.5 flex-shrink-0 focus:outline-none"
              onClick={() => setExpanded((v) => !v)}
              aria-label="Toggle criteria details"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              {/* dots preview */}
              <div className="flex gap-0.5">
                {criteria.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: c.achieved
                        ? "#4ade80"
                        : c.inProgress
                        ? "#facc15"
                        : "rgba(71,85,105,0.6)",
                      boxShadow: c.achieved ? "0 0 4px #4ade8088" : "none",
                    }}
                  />
                ))}
              </div>
              <span
                style={{
                  fontSize: 10,
                  color: "#64748b",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {achievedCount}/{criteria.length}
              </span>
              <ChevronDown
                style={{
                  width: 13,
                  height: 13,
                  color: "#64748b",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </button>
          </div>

          {/* ── MOBILE EXPANDED PANEL ─────────────────────────────── */}
          <div
            className="md:hidden overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: expanded ? 120 : 0,
              opacity: expanded ? 1 : 0,
            }}
          >
            <div
              className="flex flex-wrap gap-1.5 px-3 pb-3 pt-1"
              style={{ borderTop: "1px solid rgba(51,65,85,0.3)" }}
            >
              {criteria.map((c) => (
                <CriterionPill
                  key={c.id}
                  achieved={c.achieved}
                  inProgress={c.inProgress}
                  label={c.label}
                  score={c.score}
                  maxScore={c.maxScore}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenStrengthBar;